import React from 'react';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';
import {ControlButton, TscButtonType, TscThemeName, TscThemeNames, TscStyles} from '@techsmith/tsc-cloud-style-guide';
import TabContentWrapper from '../TabContentWrapper';
import cssConstants from '../../../constants/cssConstants';
import CopyMediaModal from '../../modals/CopyMediaModal';
import EditCopyImage from '../../../static/svg/editMediaCopy.svg';
import EditOriginalImage from '../../../static/svg/editMediaOriginal.svg';
import '../../../static/css/modal.less';
import {IMediaDetails} from '../../../model/mediaDetails';
import withMemoizedContexts from '../../../context/contextContainerHoC';
import {themeStore} from '../../../context/themeProvider';
import {mediaStore} from '../../../context/mediaProvider';
import UserOverQuotaModal from '../../modals/UserOverQuotaModal';
import {BasicModalPortal} from '../../util/ModalPortalHelper';

const EditMediaContainer = styled.div`
   display: flex;

   @media screen and (max-width: ${cssConstants.tabBreakpoint}px) {
      flex-direction: column;
   }
`;

const PaneContainer = styled.div`
   flex: 1 1 45%;
   display: flex;
   flex-direction: column;
   align-items: center;
   margin: 0 2rem;

   @media screen and (max-width: ${cssConstants.headerBreakpoint}px) {
      margin: 0;
   }

   @media screen and (max-width: ${cssConstants.tabBreakpoint}px) {
      flex: 1 0 auto;
   }
`;

const ImageContainer = styled.div`
   height: 8rem;
   display: flex;
   align-items: center;

   @media screen and (max-width: ${cssConstants.tabBreakpoint}px) {
      display: none;
   }
`;

const Header = styled.h2`
   font-size: 1rem;
   font-weight: 600;
`;

const Description = styled.p<{theme: TscThemeNames}>`
   flex-grow: 1;
   font-size: 0.875rem;
   text-align: center;
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.lightMedium : TscStyles.color.text.medium};
   margin-top: 0;
   width: 100%;
`;

const DividerContainer = styled.div`
   display: flex;
   flex-direction: column;
   justify-content: space-between;
   align-items: center;
   margin: 0 1.25rem;

   @media screen and (max-width: ${cssConstants.tabBreakpoint}px) {
      flex-direction: row;
      margin: 1rem 0 0;
   }
`;

const VerticalBar = styled.div<{theme: TscThemeNames}>`
   flex-grow: 1;
   width: 1px;
   background-color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.lightMedium : TscStyles.color.ui.dawn.medium};

   @media screen and (max-width: ${cssConstants.tabBreakpoint}px) {
      width: auto;
      height: 1px;
   }
`;

const DividingPhrase = styled.span`
   font-size: 0.875rem;
   font-weight: 600;
   margin: 0.5rem 0;

   @media screen and (max-width: ${cssConstants.tabBreakpoint}px) {
      margin: 0 0.5rem;
   }
`;

interface IEditMediaTabState {
   showCopyMediaModal: boolean;
   showUserQuotaCheck: boolean;
   displayIEWarning: boolean;
}

export const EditMediaTabBase: React.FC<IStateMappedProps> = ({theme, Hash, Url}) => {
   const {t} = useTranslation();

   const [editMediaTabState, setEditMediaTabState] = React.useState<IEditMediaTabState>({
      showCopyMediaModal: false,
      showUserQuotaCheck: false,
      displayIEWarning: false
   });

   const isIE11 = !!(window as any).MSInputMethodContext && !!document.DOCUMENT_NODE;

   const onCopyMediaModalClose = (copiedMedia?: IMediaDetails) => {
      setEditMediaTabState({...editMediaTabState, showCopyMediaModal: false});
      if (copiedMedia) {
         window.location.href = `/editor/${copiedMedia.Hash}`;
      }
   };

   const copyMediaButtonClicked = () => {
      setEditMediaTabState({...editMediaTabState, showCopyMediaModal: true});
   };

   const editCopyButton = (
      <UserOverQuotaModal>
         <ControlButton
            themeName={theme}
            buttonType={TscButtonType.primary}
            testId="edit-media-copy"
            label={t('editMedia.copy.callToAction')}
            onClick={isIE11 ? () => setEditMediaTabState({...editMediaTabState, displayIEWarning: true}) : copyMediaButtonClicked}
            width={cssConstants.defaultButtonWidth}
         />
      </UserOverQuotaModal>
   );

   const editOriginalButton = (
      <UserOverQuotaModal>
         <ControlButton
            themeName={theme}
            buttonType={TscButtonType.primary}
            testId="edit-media-original"
            label={t('editMedia.original.callToAction')}
            href={isIE11 ? '' : `/editor/${Hash}`}
            // eslint-disable-next-line no-undefined
            onClick={isIE11 ? () => setEditMediaTabState({...editMediaTabState, displayIEWarning: true}) : undefined}
            width={cssConstants.defaultButtonWidth}
         />
      </UserOverQuotaModal>
   );

   const cancelRemoveAlertModal: React.JSX.Element = (
      <BasicModalPortal
         themeName={theme}
         title={t('editMedia.ieWarningModal.title')}
         onClose={() => setEditMediaTabState({...editMediaTabState, displayIEWarning: false})}
         width={cssConstants.defaultModalWidth}
      >
         <div className="modal-body">
            <div className="form-body themeable-section">
               <div className="form-field-group">
                  <span>{t('editMedia.ieWarningModal.text')}</span>
               </div>
               <div className="button-group">
                  <ControlButton
                     themeName={theme}
                     buttonType={TscButtonType.primary}
                     width={cssConstants.defaultButtonWidth}
                     onClick={() => setEditMediaTabState({...editMediaTabState, displayIEWarning: false})}
                     label={t('general.close')}
                  />
               </div>
            </div>
         </div>
      </BasicModalPortal>
   );

   return (
      <TabContentWrapper showSpinner={false} showError={false}>
         <EditMediaContainer>
            <PaneContainer>
               <ImageContainer>
                  <EditCopyImage />
               </ImageContainer>
               <Header>{t('editMedia.copy.title')}</Header>
               <Description theme={theme}>{t('editMedia.copy.description')}</Description>
               {editCopyButton}
               {editMediaTabState.showCopyMediaModal && <CopyMediaModal onClose={onCopyMediaModalClose} hideConfirmationButtons={true} selectedMediaUrl={Url} />}
            </PaneContainer>
            <DividerContainer>
               <VerticalBar theme={theme} />
               <DividingPhrase>{t('editMedia.divider')}</DividingPhrase>
               <VerticalBar theme={theme} />
            </DividerContainer>
            <PaneContainer>
               <ImageContainer>
                  <EditOriginalImage />
               </ImageContainer>
               <Header>{t('editMedia.original.title')}</Header>
               <Description theme={theme}>{t('editMedia.original.description')}</Description>
               {editOriginalButton}
            </PaneContainer>
            {editMediaTabState.displayIEWarning && cancelRemoveAlertModal}
         </EditMediaContainer>
      </TabContentWrapper>
   );
};

export interface IStateMappedProps {
   theme: TscThemeNames;
   Hash: string;
   Url: string;
}

const mapStatesToProps = (
   // eslint-disable-next-line no-empty-pattern
   {},
   {theme}: Partial<IStateMappedProps>,
   {Hash, Url}: Partial<IStateMappedProps>
): IStateMappedProps => ({theme, Hash, Url});

export default withMemoizedContexts(mapStatesToProps, themeStore, mediaStore)(EditMediaTabBase);
